import { ComponentProps } from 'react'

import { TRoomDetail, TReview } from '@/entities/room'
import { cn, formatDate } from '@/shared/lib'
import { Button, Icon } from '@/shared/ui'

import ReviewStar from '../../asset/review-star.svg'
import { ExpandableText, Item, List, SubHeading } from '../common'

export const MAX_REVIEW_DISPLAY_COUNT = 3

type Props = Pick<TRoomDetail, 'reviewList'> & {
  showAll?: boolean
  className?: string
}

export const ReviewList = ({
  reviewList: reviewListProp,
  showAll = false,
  className,
}: Props) => {
  const slicedReviewList = reviewListProp.slice(0, MAX_REVIEW_DISPLAY_COUNT)

  const reviewList = showAll ? reviewListProp : slicedReviewList

  const totalReviewCount = reviewListProp.length

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center justify-between">
        <SubHeading className="typo-body1 @md:leading-6.5!">
          이용 후기
        </SubHeading>
        <span className="typo-label1-reading font-medium text-gray-500 @md:hidden">
          후기 {totalReviewCount}개
        </span>
      </div>

      <List spacing="large" className="typo-body2 text-gray-900">
        {reviewList.map(review => (
          <ReviewItem key={review.contractId} {...review} />
        ))}
      </List>
    </div>
  )
}

export const ReviewItem = ({
  contractId,
  score,
  reviewTime,
  name,
  review,
}: TReview) => {
  return (
    <Item key={contractId} className="flex-col items-start gap-2">
      <div className="flex items-center">
        <div className="mr-4 flex items-center gap-1">
          <ReviewStar width={16} height={16} />
          <span className="typo-label1-reading font-bold text-black">
            {score}
          </span>
        </div>

        <div className="typo-label2 flex items-center gap-1">
          <span
            className={cn(
              'flex items-center gap-1 font-normal text-gray-500',
              'after:block after:w-[11px] after:text-center after:text-xs after:font-extralight after:text-purple-300 after:content-["|"]',
            )}
          >
            {formatDate({
              date: reviewTime,
              format: 'yyyy년 MM월',
            })}
          </span>
          <span className="font-medium text-gray-600">{name}</span>
        </div>
      </div>

      <div className="w-full space-y-1">
        <ExpandableText content={review} className="typo-body2" lineClamp={3} />
      </div>
    </Item>
  )
}

export const ShowAllReviewsButton = ({
  totalReviewCount,
  ...props
}: {
  totalReviewCount: number
} & ComponentProps<typeof Button>) => {
  return (
    <Button
      size="large"
      variant="outline"
      fullWidth
      className="mt-10"
      {...props}
    >
      후기 {totalReviewCount}개 전체보기
      <Icon name="arrow-right" />
    </Button>
  )
}
